import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, isDevMode, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { JwtService } from '@app/core/auth';
import { combineLatest, distinctUntilChanged, filter, map, Observable } from 'rxjs';
import { TitleService } from '@app/services/hasTitle.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserPlatformService } from '@app/services/user.service';
import { AgentService } from '@app/services/agent.service';

interface MenuItem {
  icon: string;
  label: string;
  route: string;
  badge?: string;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  template: `
    <div class="h-full flex flex-col overflow-y-auto bg-white">
      <!-- Logo -->
      @if (hastTitle) {
        <div class="px-6 py-6 border-b border-gray-200">
          <div class="flex items-center space-x-3">
            <div class="w-10 h-10 rounded-lg flex items-center justify-center gradient-primary">
            <svg fill="#FFFFFF" height="200px" width="200px" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 512.002 512.002" xml:space="preserve"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g> <g> <g> <path d="M128.257,392.533h-0.085c-4.71,0-8.491,3.823-8.491,8.533s3.866,8.533,8.576,8.533s8.533-3.823,8.533-8.533 S132.967,392.533,128.257,392.533z"></path> <path d="M179.457,392.533h-0.085c-4.71,0-8.491,3.823-8.491,8.533s3.866,8.533,8.576,8.533s8.533-3.823,8.533-8.533 S184.167,392.533,179.457,392.533z"></path> <path d="M247.468,273.067h-68.267c-4.719,0-8.533,3.823-8.533,8.533s3.814,8.533,8.533,8.533h68.267 c4.719,0,8.533-3.823,8.533-8.533S252.187,273.067,247.468,273.067z"></path> <path d="M213.334,324.267h-34.133c-4.719,0-8.533,3.823-8.533,8.533c0,4.71,3.814,8.533,8.533,8.533h34.133 c4.719,0,8.533-3.823,8.533-8.533C221.868,328.09,218.053,324.267,213.334,324.267z"></path> <path d="M358.401,298.667c-9.412,0-17.067-7.654-17.067-17.067c0-4.71-3.814-8.533-8.533-8.533s-8.533,3.823-8.533,8.533 c0,15.855,10.914,29.107,25.6,32.922v1.212c0,4.71,3.814,8.533,8.533,8.533c4.719,0,8.533-3.823,8.533-8.533v-1.212 c14.686-3.814,25.6-17.067,25.6-32.922c0-18.825-15.309-34.133-34.133-34.133c-9.412,0-17.067-7.654-17.067-17.067 c0-9.412,7.654-17.067,17.067-17.067c9.412,0,17.067,7.654,17.067,17.067c0,4.71,3.814,8.533,8.533,8.533 s8.533-3.823,8.533-8.533c0-15.855-10.914-29.107-25.6-32.922v-1.212c0-4.71-3.814-8.533-8.533-8.533 c-4.719,0-8.533,3.823-8.533,8.533v1.212c-14.686,3.814-25.6,17.067-25.6,32.922c0,18.825,15.309,34.133,34.133,34.133 c9.412,0,17.067,7.654,17.067,17.067C375.468,291.012,367.813,298.667,358.401,298.667z"></path> <path d="M333.057,392.533h-0.085c-4.71,0-8.491,3.823-8.491,8.533s3.866,8.533,8.576,8.533c4.71,0,8.533-3.823,8.533-8.533 S337.768,392.533,333.057,392.533z"></path> <path d="M435.457,409.6c4.71,0,8.533-3.823,8.533-8.533s-3.823-8.533-8.533-8.533h-0.085c-4.71,0-8.491,3.823-8.491,8.533 S430.747,409.6,435.457,409.6z"></path> <path d="M384.257,392.533h-0.085c-4.71,0-8.491,3.823-8.491,8.533s3.866,8.533,8.576,8.533c4.71,0,8.533-3.823,8.533-8.533 S388.968,392.533,384.257,392.533z"></path> <path d="M349.868,102.4h34.133c4.719,0,8.533-3.823,8.533-8.533c0-4.71-3.814-8.533-8.533-8.533h-34.133 c-4.719,0-8.533,3.823-8.533,8.533C341.334,98.577,345.149,102.4,349.868,102.4z"></path> <path d="M230.657,392.533h-0.085c-4.71,0-8.491,3.823-8.491,8.533s3.866,8.533,8.576,8.533s8.533-3.823,8.533-8.533 S235.367,392.533,230.657,392.533z"></path> <path d="M128.001,290.133h17.067c4.719,0,8.533-3.823,8.533-8.533s-3.814-8.533-8.533-8.533h-17.067 c-4.719,0-8.533,3.823-8.533,8.533S123.282,290.133,128.001,290.133z"></path> <path d="M128.001,341.333h17.067c4.719,0,8.533-3.823,8.533-8.533c0-4.71-3.814-8.533-8.533-8.533h-17.067 c-4.719,0-8.533,3.823-8.533,8.533C119.468,337.51,123.282,341.333,128.001,341.333z"></path> <path d="M469.334,0H42.668c-4.719,0-8.533,3.823-8.533,8.533v494.933c0,3.447,2.074,6.562,5.265,7.885 c1.058,0.435,2.167,0.648,3.268,0.648c2.219,0,4.403-0.87,6.033-2.5l45.167-45.167l45.167,45.167 c3.337,3.337,8.73,3.337,12.066,0l28.1-28.1l28.1,28.1c3.337,3.337,8.73,3.337,12.066,0l28.1-28.1l11.034,11.034 c3.337,3.336,8.73,3.336,12.066,0l28.1-28.1l28.1,28.1c3.337,3.336,8.73,3.336,12.066,0l19.567-19.567l19.567,19.567 c3.337,3.336,8.73,3.336,12.066,0l45.167-45.167l28.1,28.1c0.171,0.179,0.35,0.341,0.538,0.495c0,0.009,0.008,0.009,0.008,0.009 v0.009c1.399,1.169,3.174,1.894,5.112,1.98c0.316,0.009,0.631,0.009,0.964-0.009h0.009c1.724-0.128,3.311-0.751,4.599-1.749 c0.461-0.35,0.879-0.751,1.254-1.186c1.289-1.493,2.074-3.43,2.082-5.564v-0.017V8.533C477.868,3.823,474.053,0,469.334,0z M460.801,448.734l-19.567-19.567c-3.337-3.336-8.73-3.336-12.066,0l-45.167,45.167l-19.567-19.567 c-3.336-3.337-8.73-3.337-12.066,0l-19.567,19.567l-28.1-28.1c-3.336-3.337-8.73-3.337-12.066,0l-28.1,28.1L253.501,463.3 c-3.337-3.337-8.73-3.337-12.066,0l-28.1,28.1l-28.1-28.1c-1.664-1.664-3.849-2.5-6.033-2.5c-2.185,0-4.369,0.836-6.033,2.5 l-28.1,28.1l-45.167-45.167c-3.337-3.337-8.73-3.337-12.066,0l-36.634,36.634V17.067h409.6V448.734z"></path> <path d="M77.057,409.6c4.71,0,8.533-3.823,8.533-8.533s-3.823-8.533-8.533-8.533h-0.085c-4.71,0-8.491,3.823-8.491,8.533 S72.347,409.6,77.057,409.6z"></path> <path d="M128.001,238.933h17.067c4.719,0,8.533-3.823,8.533-8.533s-3.814-8.533-8.533-8.533h-17.067 c-4.719,0-8.533,3.823-8.533,8.533S123.282,238.933,128.001,238.933z"></path> <path d="M281.857,409.6c4.71,0,8.533-3.823,8.533-8.533s-3.823-8.533-8.533-8.533h-0.085c-4.71,0-8.491,3.823-8.491,8.533 S277.147,409.6,281.857,409.6z"></path> <path d="M179.201,187.733h51.2c4.719,0,8.533-3.823,8.533-8.533s-3.814-8.533-8.533-8.533h-51.2 c-4.719,0-8.533,3.823-8.533,8.533S174.482,187.733,179.201,187.733z"></path> <path d="M128.001,187.733h17.067c4.719,0,8.533-3.823,8.533-8.533s-3.814-8.533-8.533-8.533h-17.067 c-4.719,0-8.533,3.823-8.533,8.533S123.282,187.733,128.001,187.733z"></path> <path d="M264.534,221.867h-85.333c-4.719,0-8.533,3.823-8.533,8.533s3.814,8.533,8.533,8.533h85.333 c4.719,0,8.533-3.823,8.533-8.533S269.253,221.867,264.534,221.867z"></path> <path d="M128.001,102.4h136.533c4.719,0,8.533-3.823,8.533-8.533c0-4.71-3.814-8.533-8.533-8.533H128.001 c-4.719,0-8.533,3.823-8.533,8.533C119.468,98.577,123.282,102.4,128.001,102.4z"></path> </g> </g> </g> </g></svg>
            </div>
            <div>
              <h2 class="text-xl font-bold text-gray-900">Invoice</h2>
              <p class="text-sm text-gray-500">Pro Platform</p>
            </div>
          </div>
        </div>
      }

      <!-- Navigation -->
      <nav class="flex-1 px-4 py-6 space-y-1">
        <div class="mb-6">
          @if (showAdminSection$ | async) {
            <div>
              <h3 class="px-4 mb-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">{{ 'SIDEBAR.ADMIN' | translate }}</h3>
              <div class="flex flex-col space-y-1">
                @for (item of mainMenuItems$ | async; track item.route) {
                  <a
                     [routerLink]="item.route"
                     [class.active]="isRouteActive(item.route)"
                     (click)="onLinkClick()"
                     class="sidebar-link">
                    <span [innerHTML]="item.icon" class="w-5 h-5 mr-3 flex-shrink-0"></span>
                    <span class="flex-1">{{ item.label | translate}}</span>
                    @if (item.badge) {
                      <span class="ml-2 badge badge-info">{{ item.badge }}</span>
                    }
                  </a>
                }
              </div>
            </div>
          }
          @if (showAgentSection$ | async) {
            <div>
              <h3 class="px-4 my-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">{{ 'SIDEBAR.AGENT' | translate }}</h3>
              <div class="flex flex-col space-y-1">
                @for (item of agentMenuItems; track item.route) {
                  <a
                     [routerLink]="item.route"
                     [class.active]="isRouteActive(item.route)"
                     (click)="onLinkClick()"
                     class="sidebar-link">
                    <span [innerHTML]="item.icon" class="w-5 h-5 mr-3 flex-shrink-0"></span>
                    <span class="flex-1">{{ item.label | translate}}</span>
                    @if (item.badge) {
                      <span class="ml-2 badge badge-info">{{ item.badge }}</span>
                    }
                  </a>
                }
              </div>
            </div>
          }
        </div>
      </nav>

      <!-- User Profile Footer -->
      <div class="px-4 py-4 border-t border-gray-200 mt-auto">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 rounded-full flex items-center justify-center gradient-primary flex-shrink-0">
            <span class="text-white font-medium text-sm">{{getInitials(user.name)}}</span>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-900 truncate">{{user.name}}</p>
            <p class="text-xs text-gray-500 truncate">{{user.organisation}}</p>
          </div>
          <div class="w-2 h-2 bg-success rounded-full flex-shrink-0"></div>
        </div>
      </div>
    </div>
  `
})
export class SidebarComponent implements OnInit {

  @Output() linkClick = new EventEmitter<void>();
  isAgent$ : Observable<boolean>;
  isAdmin$ : Observable<boolean>;
  mainMenuItems$: Observable<MenuItem[]>;
  showAdminSection$: Observable<boolean>;
  showAgentSection$: Observable<boolean>;

  private jwtService = inject(JwtService);
  private titleService = inject(TitleService);
  public translate = inject(TranslateService);
  public userService = inject(UserPlatformService);
  private agentService = inject(AgentService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  hastTitle = false;

  user = {
    name: '',
    organisation: '',
    role: ''
  };

  mainMenuItems: MenuItem[] = [

    {
      icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>',
      label: 'SIDEBAR.ADMIN_ORDERS',
      route: '/admin-orders'
    },
    {
      icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path></svg>',
      label: 'SIDEBAR.CREATE_PRODUCT',
      route: '/product-create',
    },
  ];

    agentMenuItems: MenuItem[] = [
    {
      icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path></svg>',
      label: 'SIDEBAR.CATALOG',
      route: '/catalog',
    },
    {
      icon: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path></svg>',
      label: 'SIDEBAR.PRODUCT_SUGGESTIONS',
      route: '/product-suggestions',
    }
  ];

  constructor() {
    this.isAgent$ = this.agentService.isAgent();
    this.isAdmin$ = this.agentService.isAdmin();

    // Determine if admin section should be shown
    // Show admin section if isAdmin=true OR if both are false (default state)
    this.showAdminSection$ = combineLatest([this.isAgent$, this.isAdmin$]).pipe(
      map(([isAgent, isAdmin]) => isAdmin || (!isAgent && !isAdmin))
    );

    // Determine if agent section should be shown
    // Show agent section if isAgent=true OR if both are false (default state)
    this.showAgentSection$ = combineLatest([this.isAgent$, this.isAdmin$]).pipe(
      map(([isAgent, isAdmin]) => isAgent || (!isAgent && !isAdmin))
    );

    // Filter menu items based on agent status
    this.mainMenuItems$ = this.isAgent$.pipe(
      map(isAgent =>
        isAgent
          ? this.mainMenuItems.filter(item => item.route !== '/admin-orders')
          : this.mainMenuItems
      )
    );
  }

  ngOnInit() {

    // Re-evaluate active route after each navigation (needed for OnPush)
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.cdr.markForCheck();
    });

    if (this.agentService.isAgentValue() && isDevMode()) {
        console.log('User is an agent');
    }

    // S'abonner aux changements du token
    this.jwtService.token$
    .pipe(
      distinctUntilChanged((prev, curr) => {
        // Compare les tokens pour éviter les exécutions inutiles
        return prev?.sub === curr?.sub;
      }),
      takeUntilDestroyed(this.destroyRef)
    )
    .subscribe(decodedToken => {
      try {
        if (decodedToken && decodedToken.name && decodedToken.sub) {
          // Utilise le nom complet du token s'il existe
          this.user.name = decodedToken.name;
          const match = decodedToken.sub.match(/@([^.]+)\./);
          const organisation = match ? match[1] : "";
          this.user.organisation = decodedToken.organization ?? organisation.toUpperCase();
        } else {
          // Si pas de token, réinitialiser le nom
          this.user.name = '';
          this.user.organisation = '';
        }
      } catch (error) {
        if (isDevMode()) console.error('Error processing token in sidebar component:', error);
        this.user.name = '';
        this.user.organisation = '';
      }
    });

    // Correction: s'abonner au rôle pour mettre à jour user.role
    this.hastTitle = this.titleService.getHasTitle();

    // Forcer une mise à jour du token observable au cas où il serait déjà disponible
    setTimeout(() => {
      try {
        this.jwtService.updateTokenObservable();
      } catch (error) {
        if (isDevMode()) console.error('Error updating token observable:', error);
      }
    }, 100);


    // S'abonner à l'utilisateur connecté (filtrer les valeurs null)
    this.userService.getCurrentUser()
      .pipe(
        filter(user => user !== null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(currentUser => {
        // Optionnel : mettre à jour les infos du user
        if (currentUser) {
          this.user.name = `${currentUser.firstname} ${currentUser.surname}`;
          // this.user.role = currentUser.appId;
        }
      })
  }

  getInitials(name: string): string {
    return name.split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  onLinkClick(): void {
    this.linkClick.emit();
  }

  /**
   * Check if a route is active
   * Special handling for /dashboard route to include /day-products
   */
  isRouteActive(route: string): boolean {
    const currentUrl = this.router.url;

    // Exact match for the route
    if (currentUrl === route) {
      return true;
    }

    // For other routes, check if current URL starts with the route
    // but only if it's followed by a query parameter or fragment
    if (currentUrl.startsWith(route + '?') || currentUrl.startsWith(route + '#')) {
      return true;
    }

    return false;
  }

}
